<template>
<div>
	<template v-if="recordId">
		<div class="d-flex flex-row align-items-center justify-content-between mb-2">
			<h3>Attachments</h3>

			<div v-if="!readonly">
				<button class="btn btn-success mr-1" type="button" v-on:click="addFile">
					<i class="fa fa-upload"></i>
					Upload a File...
				</button>
				<button v-if="allowAddFromFilesTab !== false && !readonly" class="btn btn-success" type="button" v-on:click="addFileFromParent">
					<i class="fa fa-upload"></i>
					Add from Files Tab...
				</button>
			</div>
		</div>

		<div v-if="showTagFilters && fileTags && fileTags.length > 0" class="d-flex flex-column mb-3">
			<h4>Filter Files</h4>
			<label v-for="tag in fileTags" :key="tag">
				<input type="checkbox" v-model="selectedFileTags" :value="tag">
				{{tag}}
			</label>
		</div>

		<file-list-item v-for="file in mutatedFiles"
			:key="file.src + '-' + file.id"
			:id="file.fileId"
			:src="file.src"
			:thumb-src="file.thumbUrl"
			:filename="file.filename"
			:size-kb="file.sizeKb"
			:tags="file.tags"
			:added-to-labels="fileAddedToLabels(file)"
			:non-editable="readonly"
			:other-buttons="fileOtherButtons(file)"
			v-on:editFile="editFile(file)"
			v-on:deleteFile="deleteFile(file)"
			v-on:addToParentFilesTab="addFileToParentFilesTab(file)">
		</file-list-item>

		<upload-field v-if="!readonly"
			style="width:100%;height: 40px;margin-top:10px"
			:icons="['fa-plus','fa-file']"
			accept=".jpg,.jpeg,.png,.heic,.pdf"
			target="LibraryUpload/save"
			multiple="multiple"
			style-type="box"
			v-on:uploaded="fileUploaded($event,true)">
		</upload-field>
	</template>
	<p v-else>
		Please save before adding files
	</p>

	<modal-dialog
		:modal-visible="fileEditModal.visible"
		:title="fileEditModal.title"
		confirm-button-text="Save"
		:working="fileEditModal.working"
		v-on:closeModal="closeFileEditModal"
		v-on:confirm="saveFileEdit">

		<upload-field v-if="!fileEditModal.filename"
			style="width:100%;height: 40px;margin-top:10px"
			:icons="['fa-plus','fa-file']"
			accept=".jpg,.jpeg,.png,.pdf,.heic"
			target="LibraryUpload/save"
			multiple="multiple"
			style-type="box"
			v-on:uploadStarted="fileEditModal.working = true"
			v-on:uploaded="fileUploaded($event,false)">
		</upload-field>

		<field v-else
			label="Filename"
			col-class="col-md-4"
			:readonly="true"
			v-model="fileEditModal.filename">
		</field>

		<field label="Caption"
			col-class="col-md-4"
			v-model="fileEditModal.payload.caption">
		</field>

		<field v-if="fileEditModal.parentFileSave || (fileTags && fileTags.length)"
			label="Tags"
			col-class="col-md-4"
			v-model="fileEditModal.tags"
			:params="{type:'select-multi',options: fileEditTagOptions}">
		</field>

		<field v-if="showSizeOnPdfField"
			label="Size on Pdf"
			col-class="col-md-4"
			:params="{
				type: 'select',
				options: [
					{text: 'Full Width',value: 0},
					{text: '1/2 Width',value: 91},
					{text: '1/4 Width',value: 45},
				]
			}"
			v-model="fileEditModal.payload.mm_width_on_pdf">
		</field>
	</modal-dialog>

	<modal-dialog
		:modal-visible="addFileFromParentModal.visible"
		title="Add File from Files Tab"
		cancel-button-text="Done"
		:submit-visible="false"
		v-on:closeModal="addFileFromParentModal.visible = false">

		<field v-if="sourceFiles.length > 0"
			col-class="stacked"
			:params="{type: 'checklist', options: sourceFileOptions}"
			v-model="addFileFromParentModal.selectedFiles"
			v-on:change="filesFromParentChange">
		</field>
	</modal-dialog>
</div>
</template>

<script>
import Field from "@/components/Field"
import ModalDialog from "@/components/ModalDialog"
import FileListItem from "@/components/FileListItem"
import UploadField from "@/components/UploadField"

export default {
	name: "OutboundDocumentFilesTab",
	components: {
		Field,
		ModalDialog,
		FileListItem,
		UploadField
	},
	props: [
		"recordId",
		"fileModel",
		"recordForeignKey",
		"parentStoreLocation",
		"parentFileModel",
		"readonly",
		"allowAddFromFilesTab",
		"allowAddToParentFilesTab",
		"showTagFilters",
		"fileTags",
		"sizeField"
	],
	data() {
		return {
			files: [],
			selectedFileTags: [],
			fileEditModal: {
				visible: false,
				working: false,
				title: "Add File",
				tags: [],
				filename: "",
				payload: {},
				parentFileSave: false
			},
			addFileFromParentModal: {
				visible: false,
				selectedFiles: []
			},
			previousParentFileSelection: []
		}
	},
	watch: {
		recordId() {
			this.fetchFiles()
		}
	},
	computed: {
		tagOptions() {
			let tags = this.fileTags || []
			return this.optionify(tags.map(tag => ({ id: tag, value: tag })), "value")
		},
		fileEditTagOptions() {
			if (this.fileEditModal.parentFileSave)
				return this.optionify(this.$store.fileTags, "name")

			return this.tagOptions
		},
		sourceFiles() {
			if (!this.parentStoreLocation || !(this.parentStoreLocation in this.$store))
				return []

			if (this.parentFileModel in this.$store[this.parentStoreLocation] && this.$store[this.parentStoreLocation][this.parentFileModel])
				return this.$store[this.parentStoreLocation][this.parentFileModel]

			return []
		},
		sourceFileOptions() {
			return this.sourceFiles.filter(file => "UploadFile" in file && file.UploadFile).map(file => ({
				text: file.caption + ' (' + file.UploadFile.original_filename + ')',
				value: file.id
			}))
		},
		mutatedFiles() {
			let vm = this

			return this.files
				.filter(file => "LibraryUpload" in file && file.LibraryUpload)
				.map(file => {
					file.fileId = file.LibraryUpload.file_id
					file.filename = file.LibraryUpload.original_filename
					file.sizeKb = file.LibraryUpload.file_size_kb
					file.src = window.apiRoot + "resources/files/" + vm.$store.sessionToken + "/" + file.LibraryUpload.filename
					file.thumbUrl = window.apiRoot + "resources/files/" + vm.$store.sessionToken + "/" + file.LibraryUpload.thumb_filename
					file.tags = []
					file.fullUrl = file.src

					let segs = file.filename.split("?")[0].split(".")
					let ext = segs[segs.length - 1]
					if (ext.toLowerCase() == "pdf") {
						file.fullUrl += "?display=true"
						file.thumbUrl = window.apiRoot + "resources/files/" + vm.$store.sessionToken + "/" + file.LibraryUpload.filename + "?display=true"
					}

					file.tags = []

					if ("tags_json" in file) {
						try {
							file.tags = JSON.parse(file.tags_json)
						}
						catch (e) {}
					}
					else {
						//find parentFileModel record that matches this
						let parentModelMatch = this.sourceFiles.find(record => record.file_id == file.file_id)

						if (parentModelMatch) 
							file.tags = parentModelMatch.FileTag.map(tag => tag.name)
					}

					return file
				}).filter(file => {
					if (vm.selectedFileTags.length == 0)
						return true

					let found = false
					file.tags.forEach(tag => {
						if (vm.selectedFileTags.indexOf(tag) >= 0)
							found = true
					})
					return found
				})
		},
		parentFileJobField() {
			if (this.parentFileModel == "JobFile")
				return "job_id"
			if (this.parentFileModel == "ServiceFile")
				return "service_id"
			return null
		},
		parentRecordId() {
			if (!this.parentStoreLocation || !(this.parentStoreLocation in this.$store))
				return null

			let storeRecord = this.$store[this.parentStoreLocation]
			if (!storeRecord || !storeRecord.id)
				return null

			return storeRecord.id
		},
		showSizeOnPdfField() {
			if (!this.sizeField || !this.fileEditModal.filename)
				return false

			let split = this.fileEditModal.filename.split(".")
			let ext = split[split.length - 1].toLowerCase()
			return this.isImageExtension(ext)
		}
	},
	methods: {
		addFile() {
			this.fileEditModal.visible = true
			this.fileEditModal.title = "Add File"
			this.fileEditModal.parentFileSave = false
			this.fileEditModal.filename = ""
			this.fileEditModal.payload = {
				id: "",
				caption: ""
			}
			this.fileEditModal.tags = []
		},
		editFile(file) {
			this.fileEditModal.visible = true
			this.fileEditModal.title = "Edit File"
			this.fileEditModal.parentFileSave = false
			this.fileEditModal.filename = file.filename
			this.fileEditModal.payload = {
				id: file.id,
				caption: file.caption,
				mm_width_on_pdf: file.mm_width_on_pdf
			}

			try {
				this.fileEditModal.tags = JSON.parse(file.tags_json)
			}
			catch (e) {
				this.fileEditModal.tags = []
			}
		},
		closeFileEditModal() {
			this.fileEditModal.visible = false
			this.fileEditModal.parentFileSave = false
		},
		saveFileEdit() {
			let vm = this

			if (this.fileEditModal.parentFileSave) {
				let jobField = this.parentFileJobField

				if (!jobField || !this.parentRecordId) {
					vm.alertFail("Could not add file to the Files tab.")
					return
				}

				let payload = {
					file_id: this.fileEditModal.payload.file_id,
					is_file: 1,
					caption: this.fileEditModal.payload.caption,
					FileTag: this.fileEditModal.tags
				}
				payload[jobField] = this.parentRecordId

				this.fileEditModal.working = true

				this.postData(this.parentFileModel + "/save", payload, true).then(response => {
					vm.fileEditModal.working = false
					if (response.success) {
						vm.alertSaved("File added to Files tab")
						vm.closeFileEditModal()
						vm.fetchParentFiles()
					}
				})
				return
			}

			if (this.fileTags && this.fileTags.length)
				this.fileEditModal.payload.tags_json = JSON.stringify(this.fileEditModal.tags)

			this.fileEditModal.working = true

			this.postData(this.fileModel + "/save", this.fileEditModal.payload, true).then(response => {
				vm.fileEditModal.working = false
				if (response.success) {
					vm.fetchFiles()
					vm.alertSaved("File saved")
					vm.closeFileEditModal()
					vm.$emit("fileSave",vm.fileEditModal.payload)
				}
			})
		},
		fileUploaded(response, saveRecordLink) {
			let vm = this
			vm.fileEditModal.filename = response.data.original_filename
			vm.fileEditModal.working = false
			vm.fileEditModal.payload.file_id = response.data.id
			vm.fileEditModal.payload[this.recordForeignKey] = this.recordId
			delete vm.fileEditModal.payload.id

			if (saveRecordLink) {
				let payload = {
					file_id: response.data.id
				}
				payload[this.recordForeignKey] = this.recordId

				this.postData(this.fileModel + "/save", payload, true).then(saveResponse => {
					if (saveResponse.success) {
						vm.alertSaved("File uploaded")
						vm.fetchFiles()
					}
				})
			}
		},
		deleteFile(file) {
			let vm = this
			let payload = { id: file.id }
			if (this.sourceFiles.find(parentFile => parentFile.UploadFile && parentFile.UploadFile.id == file.file_id))
				payload.keepFile = true

			this.postData(this.fileModel + "/delete", payload).then(response => {
				if (response.success) {
					vm.alertSuccess("File deleted")
					vm.fetchFiles()
				}
			})
		},
		addFileFromParent() {
			this.addFileFromParentModal.visible = true
			this.previousParentFileSelection = this.addFileFromParentModal.selectedFiles.slice()
		},
		isFileOnParentFilesTab(file) {
			return !!this.sourceFiles.find(parentFile => parentFile.UploadFile && parentFile.UploadFile.id == file.file_id)
		},
		fileAddedToLabels(file) {
			if (!this.parentFileModel || !this.parentStoreLocation)
				return []

			if (this.isFileOnParentFilesTab(file))
				return ["Files Tab"]

			return []
		},
		fileOtherButtons(file) {
			if (!this.allowAddToParentFilesTab || !this.parentFileModel || !this.parentRecordId)
				return []

			if (this.isFileOnParentFilesTab(file))
				return []

			return [{
				icon: "fa-folder-open",
				text: "Add to Files Tab",
				action: "addToParentFilesTab"
			}]
		},
		addFileToParentFilesTab(file) {
			let vm = this
			let jobField = this.parentFileJobField

			if (!jobField || !this.parentRecordId) {
				vm.alertFail("Could not add file to the Files tab.")
				return
			}

			if (this.isFileOnParentFilesTab(file)) {
				vm.alertSuccess("File is already on the Files tab.")
				return
			}

			if (this.$store.fileTags.length == 0)
				this.fetchFileTagsForParentSave()

			this.fileEditModal.parentFileSave = true
			this.fileEditModal.visible = true
			this.fileEditModal.title = "Add to Files Tab"
			this.fileEditModal.filename = file.filename
			this.fileEditModal.tags = []
			this.fileEditModal.payload = {
				file_id: file.file_id,
				caption: file.caption || ""
			}
		},
		fetchFileTagsForParentSave() {
			let vm = this
			let filterField = this.parentFileModel == "ServiceFile" ? "is_for_services" : "is_for_projects"

			this.getJSON("FileTag/get", {
				filters: [[filterField, 1]]
			}).then(response => {
				if (response.success)
					vm.$store.fileTags = response.data
			})
		},
		fetchParentFiles() {
			let vm = this
			let jobField = this.parentFileJobField

			if (!jobField || !this.parentRecordId || !this.parentStoreLocation || !this.parentFileModel)
				return

			if (!(this.parentStoreLocation in this.$store))
				return

			let params = {
				filters: [[jobField, this.parentRecordId]],
				include: [
					{
						model: "UploadFile",
						fields: ["id", "slug", "type", "original_filename", "filename", "thumb_filename"]
					},
					"FileTag"
				]
			}

			this.getJSON(this.parentFileModel + "/get", params).then(response => {
				if (response.success)
					vm.$store[vm.parentStoreLocation][vm.parentFileModel] = response.data
			})
		},
		filesFromParentChange(newSelected) {
			let vm = this
			let oldSelected = this.previousParentFileSelection || []

			newSelected.forEach(id => {
				if (oldSelected.indexOf(id) >= 0)
					return

				let file = vm.sourceFiles.find(parentFile => parentFile.id == id)
				if (!file)
					return

				let payload = {
					file_id: file.UploadFile.id,
					caption: file.caption
				}
				payload[vm.recordForeignKey] = vm.recordId

				vm.postData(vm.fileModel + "/save", payload, true).then(response => {
					if (response.success)
						vm.fetchFiles()
				})
			})

			oldSelected.forEach(id => {
				if (newSelected.indexOf(id) >= 0)
					return

				let file = vm.sourceFiles.find(parentFile => parentFile.id == id)
				if (!file)
					return

				let fileFromDoc = vm.files.find(docFile => docFile.file_id == file.UploadFile.id)
				if (fileFromDoc) {
					vm.postData(vm.fileModel + "/delete", { id: fileFromDoc.id, keepFile: true }, true).then(response => {
						if (response.success)
							vm.fetchFiles()
					})
				}
			})

			this.previousParentFileSelection = newSelected.slice()
		},
		fetchFiles() {
			let vm = this
			if (!this.recordId) {
				this.files = []
				return Promise.resolve()
			}

			let params = {
				include: ["LibraryUpload"],
				filters: [
					[this.recordForeignKey, this.recordId]
				]
			}

			return this.getJSON(this.fileModel + "/get", params).then(response => {
				if (response.success) {
					vm.files = response.data
					vm.addFileFromParentModal.selectedFiles = vm.sourceFiles.filter(parentFile => {
						return !!vm.files.find(docFile => parentFile.UploadFile && docFile.file_id == parentFile.UploadFile.id)
					}).map(parentFile => parentFile.id)
					vm.previousParentFileSelection = vm.addFileFromParentModal.selectedFiles.slice()
					vm.$emit("filesChanged", vm.files)
				}
				return response
			})
		}
	},
	created() {
		this.fetchFiles()
		this.fetchParentFiles()
	}
}
</script>
